import Avatar from '@/components/common/Avatar';
import { Text } from '@/components/Typography';
import useInertiaData from '@/hooks/useInertiaData';
import usePermissions from '@/hooks/usePermissions';
import useRoutes from '@/hooks/useRoutes';
import { Academy, User } from '@/types';
import { getIconByName } from '@/utils/iconMap';
import { Popover } from '@headlessui/react';
import { Link, useForm, usePage } from '@inertiajs/react';
import clsx from 'clsx';
import {
    Briefcase,
    ChevronDown,
    FileText,
    Home,
    LogOut,
    Shield,
    User as UserIcon,
    X,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MenuItem from './MenuItem';

interface SidebarProps {
    isOpen: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen }) => {
    const { route } = useRoutes();
    const { component } = usePage();
    const { t } = useTranslation();
    const { canAccessStaffPanel } = usePermissions();
    const { static_pages, user, is_impersonating, has_active_enrollment } =
        useInertiaData();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const isImpersonating = Boolean(is_impersonating);

    const academies: Academy[] = useMemo(() => user?.academies ?? [], [user]);
    const currentAcademy = user?.current_academy as Academy | undefined;

    const { post, setData, data } = useForm({ academy_id: 0 });

    useEffect(() => {
        if (data.academy_id) {
            post('/auth/select-academy/', {
                onSuccess: () => {
                    window.location.href = '/';
                },
            });
        }
    }, [data.academy_id]);

    const dynamicMenuItems = useMemo(() => {
        if (!static_pages) return [];
        return static_pages.map((page) => ({
            id: `static-${page.id}`,
            label: page.name,
            icon: getIconByName(page.menu_icon) ?? FileText,
            href: page.url,
            visible: true,
        }));
    }, [static_pages]);

    const getActiveMenuItem = React.useCallback(() => {
        if (component === 'Account/MyAccount') return 'cursos';
        if (component?.startsWith('Courses/')) return 'cursos';
        if (component?.startsWith('Staff/')) return 'staff';

        const pathname = window.location.pathname;
        const matchedPage = dynamicMenuItems.find((item) =>
            pathname.startsWith(item.href)
        );
        if (matchedPage) return matchedPage.id;

        return 'cursos';
    }, [component, dynamicMenuItems]);

    const [activeMenuItem, setActiveMenuItem] = useState(() =>
        getActiveMenuItem()
    );

    React.useEffect(() => {
        setActiveMenuItem(getActiveMenuItem());
    }, [getActiveMenuItem]);

    React.useEffect(() => {
        const handleOpenMobileMenu = () => setIsMobileMenuOpen(true);
        window.addEventListener('openMobileMenu', handleOpenMobileMenu);
        return () =>
            window.removeEventListener('openMobileMenu', handleOpenMobileMenu);
    }, []);

    const allMenuItems = [
        {
            id: 'cursos',
            label: t('home.courses'),
            icon: Home,
            href: route('home').toString(),
            visible: true,
        },
        ...dynamicMenuItems,
        {
            id: 'staff',
            label: 'Panel de Staff',
            icon: Briefcase,
            href: '/staff/',
            visible: canAccessStaffPanel,
        },
    ];

    const menuItems = allMenuItems
        .filter((item) => item.visible)
        .map((item) => ({
            ...item,
            disabled: has_active_enrollment === false && item.id !== 'cursos',
        }));

    useEffect(() => {
        document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [isMobileMenuOpen]);

    const otherAcademies = academies.filter((a) => a.id !== currentAcademy?.id);

    const getLogoUrl = (academy: Academy) =>
        academy.logo_vertical_left_white_url ?? academy.logo_url;
    const currentLogoUrl = currentAcademy ? getLogoUrl(currentAcademy) : null;

    return (
        <>
            {/* Mobile Drawer */}
            <>
                <div
                    className={clsx(
                        'fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 lg:hidden',
                        isMobileMenuOpen
                            ? 'opacity-100'
                            : 'pointer-events-none opacity-0'
                    )}
                    onClick={() => setIsMobileMenuOpen(false)}
                />
                <div
                    className={clsx(
                        'fixed inset-y-0 left-0 z-50 flex w-64 flex-col shadow-xl transition-transform duration-300 ease-in-out lg:hidden',
                        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
                    )}
                    style={{ backgroundColor: 'var(--color-theme-sidebar-bg)' }}
                >
                    {/* Top: Logo + close */}
                    <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
                        <div className="flex items-center gap-2">
                            {otherAcademies.length > 0 ? (
                                <Popover className="relative flex items-center gap-2">
                                    {({ open }) => (
                                        <>
                                            <Popover.Button className="flex items-center gap-2 focus:outline-none">
                                                {currentLogoUrl && (
                                                    <img
                                                        src={currentLogoUrl}
                                                        alt={
                                                            currentAcademy!.name
                                                        }
                                                        className="h-7 w-auto max-w-[110px] object-contain"
                                                    />
                                                )}
                                                <ChevronDown
                                                    size={16}
                                                    className={clsx(
                                                        'shrink-0 text-white/50 transition-transform duration-200',
                                                        open && 'rotate-180'
                                                    )}
                                                />
                                            </Popover.Button>
                                            <Popover.Panel className="absolute top-full left-0 z-20 mt-2 min-w-[200px] rounded-xl border border-white/10 bg-neutral-800 p-2 shadow-xl">
                                                {otherAcademies.map(
                                                    (academy) => (
                                                        <button
                                                            key={academy.id}
                                                            onClick={() =>
                                                                setData(
                                                                    'academy_id',
                                                                    academy.id
                                                                )
                                                            }
                                                            className="flex w-full items-center rounded-lg px-3 py-2.5 transition-colors hover:bg-white/10 focus:outline-none"
                                                        >
                                                            <img
                                                                src={
                                                                    getLogoUrl(
                                                                        academy
                                                                    ) ?? ''
                                                                }
                                                                alt={
                                                                    academy.name
                                                                }
                                                                className="h-6 w-auto object-contain"
                                                            />
                                                        </button>
                                                    )
                                                )}
                                            </Popover.Panel>
                                        </>
                                    )}
                                </Popover>
                            ) : (
                                currentLogoUrl && (
                                    <img
                                        src={currentLogoUrl}
                                        alt={currentAcademy!.name}
                                        className="h-7 w-auto max-w-[110px] object-contain"
                                    />
                                )
                            )}
                        </div>
                        <button
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="rounded-lg p-2 text-white/50 hover:bg-white/10 hover:text-white"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Nav */}
                    <nav className="flex-1 space-y-1 overflow-y-auto p-4">
                        {menuItems.map((item) => (
                            <MenuItem
                                key={item.id}
                                id={item.id}
                                label={item.label}
                                icon={item.icon}
                                href={item.href}
                                isActive={activeMenuItem === item.id}
                                disabled={item.disabled}
                                onClick={(id) => {
                                    setActiveMenuItem(id);
                                    setIsMobileMenuOpen(false);
                                }}
                            />
                        ))}
                    </nav>

                    {/* Bottom: Profile + Logout */}
                    <div className="border-t border-white/10 px-3 py-2">
                        {isImpersonating && (
                            <a
                                href="/impersonate/stop/"
                                className="mb-1 flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-orange-400 transition-colors hover:bg-white/10"
                            >
                                <UserIcon size={16} />
                                <Text as="span" size="sm">
                                    Leave Impersonation
                                </Text>
                            </a>
                        )}
                        <Link
                            href={route('my-account').toString()}
                            className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-neutral-300 transition-colors hover:bg-white/10 hover:text-white"
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            {user ? (
                                <Avatar
                                    user={user as User}
                                    size="sm"
                                    className="shrink-0"
                                />
                            ) : (
                                <UserIcon size={16} className="shrink-0" />
                            )}
                            <Text as="span">
                                {t('userAccount.myAccount', 'Perfil')}
                            </Text>
                        </Link>
                        {user?.is_staff && (
                            <a
                                href="/admin/"
                                className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-neutral-300 transition-colors hover:bg-white/10 hover:text-white"
                            >
                                <Shield size={16} className="shrink-0" />
                                <Text as="span">Panel de administración</Text>
                            </a>
                        )}
                        <Link
                            href={route('logout').toString()}
                            method="post"
                            as="button"
                            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-red-400 transition-colors hover:bg-white/10 hover:text-red-300"
                        >
                            <LogOut size={16} className="shrink-0" />
                            <Text as="span">
                                {t('userAccount.logout', 'Cerrar sesión')}
                            </Text>
                        </Link>
                    </div>
                </div>
            </>

            {/* Desktop Sidebar */}
            <div
                className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col lg:flex"
                style={{
                    backgroundColor: 'var(--color-theme-sidebar-bg)',
                    transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
                    transition: 'transform 300ms ease-in-out',
                }}
            >
                {/* Top: Logo card + Academy switcher chevron + Avatar */}
                <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3">
                    {otherAcademies.length > 0 ? (
                        <Popover className="relative flex items-center gap-2">
                            {({ open }) => (
                                <>
                                    <Popover.Button className="flex items-center gap-2 focus:outline-none">
                                        {currentLogoUrl && (
                                            <img
                                                src={currentLogoUrl}
                                                alt={currentAcademy!.name}
                                                className="h-7 w-auto max-w-[110px] object-contain"
                                            />
                                        )}
                                        <ChevronDown
                                            size={16}
                                            className={clsx(
                                                'shrink-0 text-white/50 transition-transform duration-200',
                                                open && 'rotate-180'
                                            )}
                                        />
                                    </Popover.Button>
                                    <Popover.Panel className="absolute top-full left-0 z-20 mt-2 min-w-[200px] rounded-xl border border-white/10 bg-neutral-800 p-2 shadow-xl">
                                        {otherAcademies.map((academy) => (
                                            <button
                                                key={academy.id}
                                                onClick={() =>
                                                    setData(
                                                        'academy_id',
                                                        academy.id
                                                    )
                                                }
                                                className="flex w-full items-center rounded-lg px-3 py-2.5 transition-colors hover:bg-white/10 focus:outline-none"
                                            >
                                                <img
                                                    src={
                                                        getLogoUrl(academy) ??
                                                        ''
                                                    }
                                                    alt={academy.name}
                                                    className="h-6 w-auto object-contain"
                                                />
                                            </button>
                                        ))}
                                    </Popover.Panel>
                                </>
                            )}
                        </Popover>
                    ) : (
                        currentLogoUrl && (
                            <img
                                src={currentLogoUrl}
                                alt={currentAcademy!.name}
                                className="h-7 w-auto max-w-[110px] object-contain"
                            />
                        )
                    )}
                </div>

                {/* Navigation Menu */}
                <nav className="flex-1 space-y-1 overflow-y-auto p-4">
                    {menuItems.map((item) => (
                        <MenuItem
                            key={item.id}
                            id={item.id}
                            label={item.label}
                            icon={item.icon}
                            href={item.href}
                            isActive={activeMenuItem === item.id}
                            disabled={item.disabled}
                            onClick={(id) => setActiveMenuItem(id)}
                        />
                    ))}
                </nav>

                {/* Bottom: Profile + Logout */}
                <div className="border-t border-white/10 px-3 py-2">
                    {isImpersonating && (
                        <a
                            href="/impersonate/stop/"
                            className="mb-1 flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-orange-400 transition-colors hover:bg-white/10"
                        >
                            <UserIcon size={16} />
                            <Text as="span" size="sm">
                                Leave Impersonation
                            </Text>
                        </a>
                    )}

                    <Link
                        href={route('my-account').toString()}
                        className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-neutral-300 transition-colors hover:bg-white/10 hover:text-white"
                    >
                        {user ? (
                            <Avatar
                                user={user as User}
                                size="sm"
                                className="shrink-0"
                            />
                        ) : (
                            <UserIcon size={16} className="shrink-0" />
                        )}
                        <Text as="span">
                            {t('userAccount.myAccount', 'Perfil')}
                        </Text>
                    </Link>

                    {user?.is_staff && (
                        <a
                            href="/admin/"
                            className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-neutral-300 transition-colors hover:bg-white/10 hover:text-white"
                        >
                            <Shield size={16} className="shrink-0" />
                            <Text as="span">Panel de administración</Text>
                        </a>
                    )}

                    <Link
                        href={route('logout').toString()}
                        method="post"
                        as="button"
                        className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-red-400 transition-colors hover:bg-white/10 hover:text-red-300"
                    >
                        <LogOut size={16} className="shrink-0" />
                        <Text as="span">
                            {t('userAccount.logout', 'Cerrar sesión')}
                        </Text>
                    </Link>
                </div>
            </div>
        </>
    );
};

export default Sidebar;
